import React from 'react';
import moment from 'moment-timezone';
import { isEmpty, filter, get, cloneDeep, pull, keyBy } from 'lodash';
import { formatMoney as formatMoneySarsa, SecurityHelper } from 'sarsaparilla';
import * as types from '../constants';

// Remember to Create tests if you add to this file

// pass this constant into the readRule() methods as the divisionId
// to find specifically the rules that don't have a divisionId
export const RULE_NOT_SPECIFIC_TO_DIVISION = '';

export const dateDiffs = (entryDate, exitDate) => {
  if (entryDate && exitDate) {
    let start = entryDate;
    let end = exitDate;
    if (!moment.isMoment(end)) {
      end = moment(end);
    }
    if (!moment.isMoment(start)) {
      start = moment(start);
    }
    // adding a day for counting exit date
    return end.diff(start, 'days') + 1;
  }
  return 0;
};

export const nightsInRange = (entryDate, exitDate) => {
  const days = dateDiffs(entryDate, exitDate);
  const nights = days - 1;
  const dates = [];

  for (let i = 0; i < nights; i++) {
    const night = moment(entryDate).add(i, 'days');
    dates.push(night);
  }

  return dates;
};

export const readRule = (ruleName, permit, divisionId) => {
  // read indexed_rules first
  if (!isEmpty(permit) && permit.indexed_rules && permit.indexed_rules[ruleName]) {
    const indexedRule = permit.indexed_rules[ruleName];

    // if there's no divisionId specified
    // or if the rule's divisionId matches
    // return the found indexed rule
    if (divisionId === undefined || indexedRule.division_id === RULE_NOT_SPECIFIC_TO_DIVISION || (indexedRule.division_id === divisionId)) {
      return indexedRule;
    }
  }

  // otherwise, filter through the raw rules
  if (!isEmpty(permit) && permit.rules) {
    const foundRules = permit.rules.filter((rule) => {
      const sameDivision = divisionId === undefined || rule.division_id === RULE_NOT_SPECIFIC_TO_DIVISION || rule.division_id === divisionId;
      const sameName = rule.name === ruleName;
      return sameDivision && sameName;
    });

    if (foundRules.length !== 0) {
      return foundRules[0];
    }
  }

  // and if a rule still isn't found, return a missing rule object
  return {
    division_id: '',
    end_date: moment.invalid(),
    operation: '',
    permit_id: '',
    start_date: moment.invalid(),
    target: '',
    type: -1,
    value: '',
    isMissing: true
  };
};

export const hasRule = (ruleName, permit, divisionId) => {
  return !readRule(ruleName, permit, divisionId).isMissing;
};

export const readRuleAsMoment = (ruleName, permit, divisionId) => {
  const rule = readRule(ruleName, permit, divisionId);
  const timeZone = get(permit, 'time_zone', '');
  return moment.tz(rule.value, timeZone);
};

export const readRuleValue = (ruleName, permit, divisionId) => {
  return readRule(ruleName, permit, divisionId).value;
};

export const readRuleAsBoolean = (ruleName, permit, divisionId) => {
  return !!readRuleValue(ruleName, permit, divisionId);
};

export const readRuleOperation = (ruleName, permit, divisionId) => {
  return readRule(ruleName, permit, divisionId).operation;
};

export function getActiveLotteries(permit) {
  return permit.lotteries && filter(permit.lotteries, 'is_active');
}

export const sumItems = (items) => {
  if (isEmpty(items)) {
    return 0;
  }
  let total = 0;
  items.forEach((i) => {
    total += parseInt(i.amount, 10);
  });
  return total;
};

export const equipmentTypeToString = new Map();
equipmentTypeToString.set(1, "Kayak");
equipmentTypeToString.set(2, "Double Kayak");
equipmentTypeToString.set(3, "Hard Shell Kayak");
equipmentTypeToString.set(4, "Inflatable Kayak");
equipmentTypeToString.set(5, "Tandem Kayak");
equipmentTypeToString.set(6, "Cataraft");
equipmentTypeToString.set(7, "Canoe");
equipmentTypeToString.set(8, "Solo Canoe");
equipmentTypeToString.set(9, "Drift");
equipmentTypeToString.set(10, "Raft");
equipmentTypeToString.set(11, "Sweep");
equipmentTypeToString.set(12, "SUP");
equipmentTypeToString.set(13, "Kicker Motor");
equipmentTypeToString.set(14, "Paddle Board");
equipmentTypeToString.set(15, "Tube");
equipmentTypeToString.set(16, "Dory");
equipmentTypeToString.set(17, "Standing Paddle Board");
equipmentTypeToString.set(18, "Motorboat");
equipmentTypeToString.set(19, "Sand Stakes");

// snow
equipmentTypeToString.set(1001, "Snowmobile");

// ground
equipmentTypeToString.set(2001, "Vehicle");

equipmentTypeToString.set(999999, "Other");
equipmentTypeToString.set(9999999, "Unknown");

export const animalTypeToString = new Map();
animalTypeToString.set(1, "Goat");
animalTypeToString.set(2, "Horse / Pack Mule");
animalTypeToString.set(3, "Llama");
animalTypeToString.set(4, "Dog");
animalTypeToString.set(5, "Horse/Mule Riding Stock");
animalTypeToString.set(6, "Dogs or other pets");
animalTypeToString.set(7, "Burro");
animalTypeToString.set(8, "Mule");
animalTypeToString.set(9, "Riding Horse");
animalTypeToString.set(10, "Stock Horse");

// bi-directional map.  Requires that all keys and values be unique.
export const issuanceTypes = new Map();
issuanceTypes.set(types.PRIVATE, 0);
issuanceTypes.set(types.COMMERCIAL, 1);
issuanceTypes.set(types.ADMIN, 2);

// bi-directional map.  Requires that all keys and values be unique.
let issuanceStats = new Map();
issuanceStats.set(types.PERMIT_PRELIMINARY, 0);
issuanceStats.set(types.PERMIT_IN_PROGRESS, 1);
issuanceStats.set(types.LOTTERY_IN_PROGRESS, 2);
issuanceStats.set(types.LOTTERY_SUBMITTED, 3);
issuanceStats.set(types.LOTTERY_WON, 4);
issuanceStats.set(types.LOTTERY_WON_IN_PROGRESS, 5);
issuanceStats.set(types.LOTTERY_LOST, 6);
issuanceStats.set(types.LOTTERY_DECLINED, 7);
issuanceStats.set(types.LOTTERY_ACCEPTED, 8);
issuanceStats.set(types.LOTTERY_NO_RESPONSE, 9);
issuanceStats.set(types.PERMIT_COMPLETE, 10);
issuanceStats.set(types.PERMIT_ISSUED, 11);
issuanceStats.set(types.CANCELLED_NO_RELEASE, 12);
issuanceStats.set(types.PERMIT_CANCELLED_BY_USER, 13);
issuanceStats.set(types.LOTTERY_CANCELLED, 14);
issuanceStats.set(types.PERMIT_VOIDED, 15);
issuanceStats.set(types.LOTTERY_VOIDED, 16);
issuanceStats.set(types.LOTTERY_REJECTED, 17);

export const issuanceReadable = new Map();
issuanceReadable.set(types.PERMIT_PRELIMINARY, types.PERMIT_PRELIMINARY_READABLE);
issuanceReadable.set(types.PERMIT_IN_PROGRESS, types.PERMIT_IN_PROGRESS_READABLE);
issuanceReadable.set(types.PERMIT_COMPLETE, types.PERMIT_COMPLETE_READABLE);
issuanceReadable.set(types.PERMIT_ISSUED, types.PERMIT_ISSUED_READABLE);
issuanceReadable.set(types.CANCELLED_NO_RELEASE, types.PERMIT_CANCELLED_READABLE);
issuanceReadable.set(types.PERMIT_CANCELLED_BY_USER, types.PERMIT_CANCELLED_READABLE);
issuanceReadable.set(types.PERMIT_VOIDED, types.PERMIT_VOIDED_READABLE);

// Requires that all keys and values be unique.
function transformIntoBidirectionalMap(map) {
  const ret = new Map();
  map.forEach((k, v) => {
    ret.set(k, v);
    ret.set(v, k);
  });
  return ret;
}

issuanceStats = transformIntoBidirectionalMap(issuanceStats);
export const issuanceStatuses = issuanceStats;

export const getUnitCardinality = (value, singularNoun, pluralNoun) => {
  return value === 1 ? singularNoun : pluralNoun;
};

// helper methods for generating the correct singular/plural nouns
export const getDayCardinality = (value) => {
  const singularNoun = 'day';
  const pluralNoun = 'days';
  return getUnitCardinality(value, singularNoun, pluralNoun);
};

export const getPersonCardinality = (value) => {
  const singularNoun = 'person';
  const pluralNoun = 'people';
  return getUnitCardinality(value, singularNoun, pluralNoun);
};

export const ruleToErrMap = new Map();
ruleToErrMap.set('Rule violated: SeasonStart', {
  text: () => `We appreciate your eagerness and enthusiasm! However, the season has not started and there are no online reservations available at this time. Please check the "Important Dates" section for more information.`,
  formatValue: (value) => moment.unix(value).utc().format('MMMM Do, YYYY')
});
ruleToErrMap.set('Rule violated: SeasonEnd', {
  text: () => `We applaud your good taste! However, the season has ended and there are no online reservations available at this time. Please check the "Important Dates" section for more information.`,
  formatValue: (value) => moment.unix(value).utc().format('MMMM Do, YYYY')
});
ruleToErrMap.set('Rule violated: QuotaPerDay', {
  text: () => `This is a popular activity and, unfortunately, there is not enough quota available for the date(s) you selected.`,
  formatValue: (value) => value
});
ruleToErrMap.set('Rule violated: RegistrationEnd', {
  text: (ruleValue) => `Advanced registration for this permit ended on ${ruleValue}.`,
  formatValue: (value) => moment.unix(value).utc().format('MMMM Do, YYYY')
});
ruleToErrMap.set('Rule violated: HighUseSeasonStart', {
  text: (ruleValue) => `We appreciate your eagerness and enthusiasm! Unfortunately, the lottery season doesn't begin until ${ruleValue}. Please refer to the "Important Dates" section and try again when the lottery opens.`,
  formatValue: (value) => moment.unix(value).utc().format('MMMM Do, YYYY')
});
ruleToErrMap.set('Rule violated: HighUseSeasonEnd', {
  text: (ruleValue) => `You made a great selection! However, the lottery season closed on ${ruleValue}. Please refer to the "Important Dates" section and try again when the lottery reopens.`,
  formatValue: (value) => moment.unix(value).utc().format('MMMM Do, YYYY')
});
ruleToErrMap.set('Rule violated: MaxGroupSize', {
  text: (ruleValue) => `You've got a full house! Please adjust your group so that it doesn't exceed the max group size of ${ruleValue}.`,
  formatValue: (value) => value
});
ruleToErrMap.set('Rule violated: StayLimit', {
  text: (ruleValue) => `This a popular permit activity and, unfortunately, the maximum length of stay is ${ruleValue} ${getDayCardinality(ruleValue)}. Please adjust your dates.`,
  formatValue: (value) => value / (24 * 60 * 60)
});
ruleToErrMap.set('Rule violated: StayMinimum', {
  text: (ruleValue) => `Can you visit for just a little longer? The minimum length of stay is ${ruleValue} ${getDayCardinality(ruleValue)}. Please adjust your dates.`,
  formatValue: (value) => value / (24 * 60 * 60)
});
ruleToErrMap.set('Rule violated: MaxReservationsHighUseSeason', {
  text: (ruleValue) => `You have excellent taste! However, you can only register ${ruleValue} ${getUnitCardinality(ruleValue, 'time', 'times')} this season and have exceeded the max number of applications.`,
  formatValue: (value) => value
});
ruleToErrMap.set('Rule violated: MaxAccountReservationsOnDay', {
  text: (ruleValue) => `You must really like this location! However, you can only register for ${ruleValue} ${getUnitCardinality(ruleValue, 'permit', 'permits')} per day.`,
  formatValue: (value) => value
});
ruleToErrMap.set('Rule violated: HighUseRegistrationStart', {
  text: (ruleValue) => `We appreciate your eagerness and enthusiasm! However, lottery registration doesn't begin until ${ruleValue}. Please check back with us then.`,
  formatValue: (value) => moment.unix(value).utc().format('MMMM Do, YYYY')
});
ruleToErrMap.set('Rule violated: HighUseRegistrationEnd', {
  text: (ruleValue) => `You made a great selection! However, lottery registration closed on ${ruleValue}. Please refer to the "Important Dates" section and try again when the lottery reopens.`,
  formatValue: (value) => moment.unix(value).utc().format('MMMM Do, YYYY')
});
ruleToErrMap.set('Rule violated: LotteryDeadlineToConfirm', {
  text: (ruleValue) => `Unfortunately, the deadline to confirm this lottery award has passed. Confirmation for the lottery was required by ${ruleValue}.`,
  formatValue: (value) => moment.unix(value).utc().format('MMMM Do, YYYY')
});
ruleToErrMap.set('Rule violated: IsLeaderTransferable', {
  text: () => `Trust your first instinct and stick with this leader. The leader cannot be changed once a reservation is booked.`,
  formatValue: (value) => value
});
ruleToErrMap.set('Rule violated: IsCustomerTransferable', {
  text: () => `We understand plans can change, but group members can't be updated once a reservation is booked.`,
  formatValue: (value) => value
});
ruleToErrMap.set('Rule violated: CancellationDeadline', {
  text: () => `Unfortunately, the cancellation window for this reservation has passed. If you choose to cancel this reservation, you may not receive a refund or the cancellation could result in a penalty based on permit regulations.`,
  formatValue: (value) => value
});
ruleToErrMap.set('Rule violated: DisallowOvernightStock', {
  text: () => `Horses and other stock animals aren't permitted for overnight trips.`,
  formatValue: (value) => value
});
ruleToErrMap.set('Rule violated: MaxPartySize', {
  text: (ruleValue) => `You've got quite the entourage! Please adjust the total number of people and stock animals so that it doesn’t' exceed the max of ${ruleValue}.`,
  formatValue: (value) => value
});
ruleToErrMap.set('Rule violated: ActiveIssuances', {
  text: (ruleValue) => `You may only have ${ruleValue} active reservations at this location.`,
  formatValue: (value) => value
});
ruleToErrMap.set('Rule violated: MinGroupSize', {
  text: (ruleValue) => `You must have at least ${ruleValue} group members to complete this reservation.`,
  formatValue: (value) => value
});

// Takes raw text of error string and formats to a friendly error message
export const formatErrorMessage = (errString) => {
  const stringArr = [];
  // If last character is a semicolon, remove it before splitting to avoid creating
  // additional (empty) element in errList
  const errList = errString.replace(/;\s*$/, '').split(';');
  errList.forEach((err) => {
    const nameToValue = err.split('|'); // RuleName|Value, i.e. SeasonStart|4823827
    if (ruleToErrMap.get(nameToValue[0])) {
      const ruleObj = ruleToErrMap.get(nameToValue[0]);
      const ruleValue = nameToValue[1] ? ruleObj.formatValue(parseInt(nameToValue[1], 10)) : '';
      stringArr.push(ruleObj.text(ruleValue));
    } else {
      stringArr.push(err);
    }
  });
  return stringArr.join('\n');
};

export const createAvailabilityRangeMap = (startDate, endDate) => {
  const dateMap = {};
  const start = moment.isMoment(startDate) ? startDate.utc() : moment(startDate, 'MM/DD/YYYY').utc();
  const end = moment.isMoment(endDate) ? endDate.utc() : moment(endDate, 'MM/DD/YYYY').utc();
  const dateDifference = end.diff(start, 'days') + 1;
  if (dateDifference > 0) {
    Array.from(new Array(dateDifference), (day, i) => {
      const d = start.clone();
      dateMap[d.add(i, 'day').format('MM/DD/YYYY')] = true;
      return d;
    });
  }
  return dateMap;
};

export const feeDisplayModels = new Map();
feeDisplayModels.set(types.ONE_FEE, 0);
feeDisplayModels.set(types.ADD_ON_FEE, 1);

export const feeCalculationModels = new Map();
feeCalculationModels.set(types.NO_USE_FEE, 0);
feeCalculationModels.set(types.FLAT_USE_FEE, 1);
feeCalculationModels.set(types.PER_GROUP_PER_NIGHT_FEE, 2);
feeCalculationModels.set(types.PER_MEMBER_FEE, 3);
feeCalculationModels.set(types.PER_MEMBER_PER_NIGHT_FEE, 4);
feeCalculationModels.set(types.PER_MEMBER_PER_DAY_FEE, 5);
feeCalculationModels.set(types.PER_WATERCRAFT_FEE, 6);
feeCalculationModels.set(types.PER_GROUP_PER_DAY_FEE, 7);
feeCalculationModels.set(types.PER_GROUP_PER_NIGHT_CUSTOM_RANGE_HIGH_USE_ONLY_FEE, 8);

export const formatCurrency = (amount) => formatMoneySarsa(amount / 100);

export const getIds = (ids) => {
  const idObj = {};
  const issIdAndPermitId = ids.split(types.RES_ID_DELIMITER);
  if (issIdAndPermitId.length === 2) {
    idObj.issuanceId = issIdAndPermitId[0];
    idObj.permitId = issIdAndPermitId[1];
  }
  return idObj;
};

export const stringifyMoment = (m) => ((m !== null && moment.isMoment(m)) ? moment(m).format('MM/DD/YYYY') : '');

export const getValueByStringPath = (obj, path) => {
  return () => get(obj, path);
};

export const checkValidationStyle = (validation, field) => (validation[field] ? 'rec-error' : '');

export const getValidationErrorStyle = (validation, field, optionalClassName) => {
  let className = 'rec-error';
  if (optionalClassName) {
    className = optionalClassName;
  }
  if (Array.isArray(field)) {
    return field.some((str) => validation[str]) ? className : '';
  }
  return (validation[field] ? className : '');
};

// check division status
export const isDivisionOpen = (status) => {
  return status === types.DIVISION_STATUS.OPEN;
};

// returns the first prop in the divisions map (not guaranteed to be the same if there are multiple divisions)
export const getFirstDivision = (permitDivisions) => {
  for (const prop in permitDivisions) {
    // prevent traversing down the object prototype
    if (!permitDivisions.hasOwnProperty(prop)) {
      continue;
    }
    return permitDivisions[prop];
  }
  // no divisions, error
  return null;
};

export const getIssuanceDivision = (issuance, permit) => {
  // gets the division from the permit, specified by the issuance, or the first division if one not specified
  // or null if nothing.

  const divisions = get(permit, 'divisions', {});
  const divisionId = get(issuance, 'division_id');
  return get(divisions, divisionId, getFirstDivision(divisions));
};

// returns an open division, otherwise returns the first division if there are none open
export const getDivision = (permitDivisions) => {
  for (const id in permitDivisions) {
    if (!Object.prototype.hasOwnProperty.call(permitDivisions, id)) {
      continue;
    }
    const division = permitDivisions[id];
    if (isDivisionOpen(division.status)) {
      return division;
    }
  }

  return getFirstDivision(permitDivisions);
};

// Gets props.currentDate if it exists, otherwise gets the current time.
// Useful for faking what time it is for debugging purposes
export const getCurrentTime = (props) => {
  if (props.currentDate) {
    return moment.isMoment(props.currentDate) ? props.currentDate.utc() : moment(props.currentDate).utc();
  }

  let currentDate = moment().utc();

  // enable timemachine date query param only in dev
  if (process.env.NODE_ENV === 'development') {
    currentDate = moment(get(props, 'location.query.date', moment())).utc();
  }

  return currentDate;
};

// sales channel types
export const salesChannelTypes = new Map();
salesChannelTypes.set(types.ONLINE, 0);
salesChannelTypes.set(types.FIELD_SALES, 1);
salesChannelTypes.set(types.CALL_CENTER, 2);

// convert sales channel type enum to string literal
export const salesChannelTypeToString = new Map();
salesChannelTypeToString.set(0, 'Online');
salesChannelTypeToString.set(1, 'Field Sales');
salesChannelTypeToString.set(2, 'Call Center');

// check issuance status for bypassing payment
export const canIssuanceStatusBypassPayment = (issuanceStatus) => {
  return issuanceStatus === issuanceStatuses.get(types.LOTTERY_WON)
    || issuanceStatus === issuanceStatuses.get(types.LOTTERY_WON_IN_PROGRESS)
    || issuanceStatus === issuanceStatuses.get(types.PERMIT_COMPLETE);
};

// permit types
export const permitTypes = new Map();
permitTypes.set(types.PERMIT_TYPE_RIVER, 0);
permitTypes.set(types.PERMIT_TYPE_HIKING, 1);
permitTypes.set(types.PERMIT_TYPE_CAMPING, 2);
permitTypes.set(types.PERMIT_TYPE_CLIMBING, 3);
permitTypes.set(types.PERMIT_TYPE_WILDLIFE, 4);
permitTypes.set(types.PERMIT_TYPE_ROADENTRY, 5);
permitTypes.set(types.PERMIT_TYPE_EVENT, 6);
permitTypes.set(types.PERMIT_TYPE_BOAT, 7);
permitTypes.set(types.PERMIT_TYPE_SNOWMOBILE, 8);
permitTypes.set(types.PERMIT_TYPE_WILDERNESS, 9);

// convert permit type enum to string literal
export const permitCategoriesToString = new Map();
permitCategoriesToString.set(0, 'River');
permitCategoriesToString.set(1, 'Hiking');
permitCategoriesToString.set(2, 'Camping');
permitCategoriesToString.set(3, 'Climbing');
permitCategoriesToString.set(4, 'Wildlife');
permitCategoriesToString.set(5, 'Road Entry');
permitCategoriesToString.set(6, 'Event');
permitCategoriesToString.set(7, 'Boat');
permitCategoriesToString.set(8, 'Snowmobile');

// helper methods for checking/retrieving rules
export const hasLotteryPreferenceSelectionLimit = (permit, divisionId) => {
  return hasRule(types.LOTTERY_PREFERENCE_SELECTION_LIMIT, permit, divisionId);
};
export const getLotteryPreferenceSelectionLimit = (permit, divisionId) => {
  return parseInt(readRuleValue(types.LOTTERY_PREFERENCE_SELECTION_LIMIT, permit, divisionId), 10);
};

export const getLotteryPreferenceType = (permit, divisionId) => {
  return parseInt(readRuleValue(types.LOTTERY_PREFERENCES_TYPE, permit, divisionId), 10);
};

export const hasLotteryPreferenceDateDivisionDuration = (permit, divisionId) => {
  return getLotteryPreferenceType(permit, divisionId) === types.LOTTERY_PREFERENCE_TYPES.DATE_DIVISION_DURATION;
};

export const hasLotteryPreferenceDateDivisionGroupSize = (permit, divisionId) => {
  return getLotteryPreferenceType(permit, divisionId) === types.LOTTERY_PREFERENCE_TYPES.DATE_DIVISION_GROUP_MIN_MAX ||
    getLotteryPreferenceType(permit, divisionId) === types.LOTTERY_PREFERENCE_TYPES.DATE_DIVISION_GROUP_MAX;
};

export const getAllReservedLotteryStatuses = () => {
  const lotteryStatuses = [];
  lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_SUBMITTED));
  lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_WON));
  lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_WON_IN_PROGRESS));
  lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_LOST));
  lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_DECLINED));
  lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_ACCEPTED));
  lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_NO_RESPONSE));
  lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_CANCELLED));
  lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_VOIDED));
  lotteryStatuses.push(issuanceStatuses.get(types.LOTTERY_REJECTED));
  return lotteryStatuses;
};

export const AVAILABILITY_FILTERS = (permit) => {
  return [
    {
      type: types.GROUP_AVAILABILITY_DAILY_KEY_NAME,
      display: types.FILTER_NUMERICAL,
      label: 'Groups',
      labelAlt: 'Groups',
      initialValue: 1,
      minValue: 1,
      showStandalone: false,
      doNotRender: true
    },
    {
      type: types.GROUP_AVAILABILITY_KEY_NAME,
      display: types.FILTER_NUMERICAL,
      label: 'Groups',
      labelAlt: 'Groups',
      initialValue: 1,
      minValue: 1,
      maxValue: 1,
      showStandalone: false,
      doNotRender: true
    },
    {
      type: types.MEMBER_AVAILABILITY_KEY_NAME,
      display: types.FILTER_NUMERICAL,
      label: 'People',
      labelAlt: 'Group Size',
      initialValue: null,
      missingInputError: 'Please enter the number of group members',
      minValue: 1,
      maxValue: readRuleValue('MaxGroupSize', permit) === '' ? null : parseInt(readRuleValue('MaxGroupSize', permit), 10),
      showStandalone: true
    },
    {
      type: types.WATERCRAFT_AVAILABILITY_KEY_NAME,
      display: types.FILTER_NUMERICAL,
      label: 'Watercraft',
      labelAlt: 'Watercraft',
      initialValue: null,
      missingInputError: 'Please select watercraft amount',
      minValue: 1,
      maxValue: readRuleValue('MaxWatercraftPerReservation', permit) === '' ? null : parseInt(readRuleValue('MaxWatercraftPerReservation', permit), 10),
      showStandalone: true
    }
  ];
};

export const addFilterValues = (filters) => {
  let item = {
    equipments: [],
    group_info: []
  };
  for (const key in filters) {
    if (!Object.prototype.hasOwnProperty.call(filters, key)) {
      continue;
    }

    if (key === types.WATERCRAFT_AVAILABILITY_KEY_NAME) {
      const equipmentInfo = {
        equipments: []
      };
      equipmentInfo.equipments.push({ type: 0, qty: parseInt(filters[key], 10) });
      item = Object.assign(item, equipmentInfo);
    } else if (key === types.MEMBER_AVAILABILITY_KEY_NAME) {
      const memberInfo = {
        group_info: []
      };
      for (let i = 0; i < filters[key]; i++) {
        memberInfo.group_info.push({
          first_name: '',
          middle_name: '',
          last_name: ''
        });
      }
      item = Object.assign(item, memberInfo);
    }
  }
  return item;
};

export function getDisplayOrderNumber(orderId) {
  if (orderId && typeof orderId === 'string') {
    return orderId.substring(orderId.length - 6);
  }
  return orderId;
}

export function calculateEntrancesExits(locations, launchDetail, divisionId, divisions) {
  // divisionId and divisions are optionally arguments
  let entrances = filter(locations, { is_entry: true });
  let exits = filter(locations, { is_exit: true });
  const locationMap = keyBy(locations, 'id');

  if (divisionId && divisions) {
    const division = divisions[divisionId];
    if (division) {
      if (division.entry_ids) {
        entrances = division.entry_ids.map((entryId) => locationMap[entryId]);
      }
      if (division.exit_ids) {
        exits = division.exit_ids.map((exitId) => locationMap[exitId]);
      }
    }
  }

  if (entrances.length === 1 && exits.length === 1) {
    return {
      validEntrances: entrances,
      validExits: exits,
      launchDetail: {
        ...launchDetail,
        entryLocationId: entrances[0].id,
        exitLocationId: exits[0].id
      }
    };
  } else if (entrances.length === 1) {
    return {
      validEntrances: entrances,
      validExits: exits,
      launchDetail: {
        ...launchDetail,
        entryLocationId: entrances[0].id
      }
    };
  } else if (exits.length === 1) {
    return {
      validEntrances: entrances,
      validExits: exits,
      launchDetail: {
        ...launchDetail,
        exitLocationId: exits[0].id
      }
    };
  }
  return {
    validEntrances: entrances,
    validExits: exits
  };
}

export function filterCommercial(locations) {
  return filter(locations, (i) => i.name.includes('Commercial'));
}

export function filterNotCommercial(locations) {
  return filter(locations, (i) => !i.name.includes('Commercial'));
}

export function calculateIssueStations(locations, isCommercial, isFieldSales) {
  let issueStations = filter(locations, { is_issue_station: true });

  // field sales user creating a commercial reservation can only select commercial stations (DEF-3727)
  if (isFieldSales) {
    if (isCommercial) {
      issueStations = filterCommercial(issueStations);
    }
    // field sales user creating non-commercial reservation can select ALL stations (including commercial) (DEF-5208)
  } else if (SecurityHelper.isCommercialAccount()) {
    issueStations = filterCommercial(issueStations);
  } else {
    issueStations = filterNotCommercial(issueStations);
  }

  if (issueStations.length === 1) {
    return {
      validIssueStations: issueStations,
      issueStationDetail: {
        selectedStationId: issueStations[0].id
      }
    };
  }
  return {
    validIssueStations: issueStations
  };
}

export function hasLotteryApplication(permit, issuances) {
  const highUseSeasonStart = readRuleAsMoment('HighUseSeasonStart', permit);
  const highUseSeasonEnd = readRuleAsMoment('HighUseSeasonEnd', permit);

  for (let i = 0; i < issuances.length; i++) {
    const iss = issuances[i];
    if (moment(iss.start_date).isAfter(highUseSeasonStart) && moment(iss.end_date).isBefore(highUseSeasonEnd)) {
      return true;
    }
  }
  return false;
}

export const passTypes = {
  None: 'None',
  LocalPass: 'Local Pass',
  InteragencyAccess: 'Interagency Access',
  InteragencySenior: 'Interagency Senior'
};

export const defaultPassOptions = [
  passTypes.None,
  passTypes.LocalPass,
  passTypes.InteragencyAccess,
  passTypes.InteragencySenior
];

export function loadPassInfo(permit) {
  const passOptions = cloneDeep(defaultPassOptions);

  if (!hasRule('LocalPass', permit)) {
    pull(passOptions, passTypes.LocalPass);
  }

  return passOptions;
}

export const buttonLabels = {
  ProceedToCheckout: 'Proceed to Checkout',
  RemoveFromCart: 'Remove from Cart',
  SaveChanges: 'Save Changes',
  CancelChanges: 'Cancel Changes',
  Cancel: 'Cancel'
};

export function renderList(items, tag) {
  if (!items) {
    return '';
  }
  return items.map((item) => {
    if (tag === 'li') {
      return (<li key={item}>{item}</li>);
    } else if (tag === 'p') {
      return (<p key={item}>{item}</p>);
    }
    return '';
  });
}

export function getRecareaName(recarea) {
  let recAreaName = 'Recreation Area';
  if (recarea && recarea[0]) {
    if (recarea[0].name) {
      recAreaName = recarea[0].name;
    }
  }
  return recAreaName;
}

export function getDivisionName(permit, iss) {
  let name = 'Requester Name';
  if (permit && permit.divisions && iss && iss.division_id) {
    const division = permit.divisions[iss.division_id];
    if (division) {
      name = division.name;
    }
  }
  return name;
}

export function convertIssuanceToGroupMembersObj(groupInfo) {
  const result = [];
  if (groupInfo) {
    for (const member of groupInfo) {
      result.push({
        fName: member.first_name,
        mName: member.middle_name,
        lName: member.last_name,
        startDate: member.start_date,
        endDate: member.end_date,
        passholderType: member.fee_type,
        passholderId: member.passholder_id,
        remark: member.remark,
        isLeader: member.is_leader
      });
    }
  }
  return result;
}

const resizeGroupMembers = (groupMembersArray, newSize) => {
  if(newSize > groupMembersArray?.length) {
    // if more group members added, add some empty for use
    return [
      ...groupMembersArray,
      ...Array(newSize - groupMembersArray.length).fill().map(() => ({
        first_name: '',
        middle_name: '',
        last_name: '',
        remark: '',
      }))
    ];
  }
  else if (newSize < groupMembersArray?.length) {
    return groupMembersArray.slice(0, newSize)
  }
  return [...groupMembersArray];
}


export const modifyIssuanceGroupSize = (issuance, groupSize) => {
  return {
    ...issuance,
    group_info: resizeGroupMembers(issuance.group_info, groupSize),
    group_size: groupSize,
  };
}
